import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  PieChart, TrendingUp, TrendingDown, Activity, Wallet, Layers,
  Bot, Target, RefreshCw, ArrowRight, Circle, BarChart3,
  DollarSign, Globe, Link2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXCHANGE_COLORS = {
  'Pump.fun': '#00D18C',
  'Raydium': '#2B6DEF',
  'Jupiter': '#19FB9B',
  'Uniswap': '#FF007A',
  'PancakeSwap': '#D1884F',
};

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-1.5 w-full bg-[hsl(220,16%,14%)] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

function SparkLine({ data, color = '#E8520F' }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.amount || 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const h = 32;
  const w = 120;
  const points = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

export default function Portfolio() {
  const ctx = useOutletContext() || {};
  const { panicMode } = ctx;
  const { isSubUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPortfolio = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/portfolio/overview`, { withCredentials: true });
      setData(res);
    } catch (err) {
      console.error('Portfolio fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 5000);
    return () => clearInterval(interval);
  }, [fetchPortfolio]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="portfolio-loading">
        <RefreshCw className="w-8 h-8 text-[#E8520F] animate-spin" />
      </div>
    );
  }

  const { summary, token_performance, exchange_performance, exchange_connections, wallets, earnings_history, recent_bots } = data;
  const maxTokenVol = Math.max(...(token_performance.map(t => t.total_volume) || [1]), 1);
  const maxExVol = Math.max(...(exchange_performance.map(e => e.total_volume) || [1]), 1);

  // Choose nav target based on role for "Manage" buttons
  const exchangeManagePath = isSubUser ? '/trade/exchange-login' : '/dashboard/exchange-login';

  return (
    <div className="space-y-6" data-testid="portfolio-overview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <PieChart className="w-7 h-7 text-[#E8520F]" />
            Portfolio Overview
          </h1>
          <p className="text-[hsl(215,16%,56%)] text-sm">Real-time aggregation of all positions, bots, and earnings</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPortfolio} className="border-[hsl(220,16%,18%)] text-white bg-transparent hover:bg-[hsl(220,16%,16%)]" data-testid="refresh-portfolio">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Top-level KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Active Bots', value: summary.active_bots, sub: `of ${summary.total_bots} total`, icon: Bot, iconBg: 'bg-[#E8520F]/10', iconColor: 'text-[#E8520F]' },
          { label: 'Total Volume', value: `$${summary.total_volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, sub: 'across all bots', icon: BarChart3, iconBg: 'bg-[#00CED1]/10', iconColor: 'text-[#00CED1]' },
          { label: 'Total Earned', value: `$${summary.total_earned.toFixed(2)}`, sub: 'lifetime', icon: DollarSign, iconBg: 'bg-[#10B981]/10', iconColor: 'text-[#10B981]' },
          { label: 'Total PnL', value: `${summary.total_pnl >= 0 ? '+' : ''}$${summary.total_pnl.toFixed(2)}`, sub: summary.total_pnl >= 0 ? 'profit' : 'loss', icon: summary.total_pnl >= 0 ? TrendingUp : TrendingDown, iconBg: summary.total_pnl >= 0 ? 'bg-[#10B981]/10' : 'bg-[#EF4444]/10', iconColor: summary.total_pnl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]' },
          { label: 'Available', value: `$${summary.available_balance.toFixed(2)}`, sub: 'withdrawable', icon: Wallet, iconBg: 'bg-[#F59E0B]/10', iconColor: 'text-[#F59E0B]' },
          { label: 'Exchanges', value: summary.connected_exchanges, sub: `connected`, icon: Globe, iconBg: 'bg-[#6366F1]/10', iconColor: 'text-[#6366F1]' },
        ].map((kpi, i) => (
          <Card key={i} className="border-[hsl(220,16%,18%)] shadow-none hover:shadow-sm transition-shadow" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg ${kpi.iconBg} flex items-center justify-center`}>
                  <kpi.icon className={`w-4 h-4 ${kpi.iconColor}`} />
                </div>
              </div>
              <p className="text-lg font-bold text-white font-mono">{kpi.value}</p>
              <p className="text-[10px] text-[hsl(215,16%,45%)]">{kpi.label} — {kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Middle row — Token Performance + Earnings Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Token Performance (2/3) */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none lg:col-span-2" data-testid="token-performance-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#E8520F]" /> Token Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {token_performance.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 text-[10px] text-[hsl(215,16%,45%)] uppercase tracking-wider px-1">
                  <span className="col-span-3">Token</span>
                  <span className="col-span-2">Exchange</span>
                  <span className="col-span-1 text-center">Bots</span>
                  <span className="col-span-2 text-right">Volume</span>
                  <span className="col-span-2 text-right">PnL</span>
                  <span className="col-span-2">Bar</span>
                </div>
                {token_performance.map((t, i) => (
                  <div key={t.symbol} className="grid grid-cols-12 gap-2 items-center py-2 px-1 rounded-lg hover:bg-[hsl(220,16%,14%)] transition-colors"
                    data-testid={`token-row-${t.symbol}`}>
                    <div className="col-span-3 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-[#E8520F]/10 flex items-center justify-center text-xs font-bold text-[#E8520F]">
                        {t.symbol.charAt(0)}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-white">{t.symbol}</p>
                        <p className="text-[10px] text-[hsl(215,16%,45%)]">{t.total_trades} trades</p>
                      </div>
                    </div>
                    <p className="col-span-2 text-xs text-[hsl(215,16%,56%)] truncate">{t.exchange}</p>
                    <div className="col-span-1 flex items-center justify-center gap-1">
                      {t.volume_bots > 0 && <span className="text-[10px] px-1 py-0.5 rounded bg-[#00CED1]/10 text-[#00CED1]">{t.volume_bots}V</span>}
                      {t.lure_bots > 0 && <span className="text-[10px] px-1 py-0.5 rounded bg-[#E8520F]/10 text-[#E8520F]">{t.lure_bots}L</span>}
                    </div>
                    <p className="col-span-2 text-sm text-right font-mono text-white">${t.total_volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className={`col-span-2 text-sm text-right font-mono ${t.realized_pnl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {t.realized_pnl >= 0 ? '+' : ''}{t.realized_pnl.toFixed(2)}
                    </p>
                    <div className="col-span-2">
                      <MiniBar value={t.total_volume} max={maxTokenVol} color="#E8520F" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-[hsl(215,16%,45%)]">
                <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No token positions yet</p>
                <p className="text-xs mt-1">Start a bot on a DeFi exchange to see performance</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Earnings Trend (1/3) — Sub-User Bots tab REMOVED */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none" data-testid="earnings-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#10B981]" /> Earnings Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-[#10B981]/5 to-[#00CED1]/5 rounded-xl border border-[#10B981]/10">
              <p className="text-xs text-[hsl(215,16%,56%)] mb-1">Lifetime Earnings</p>
              <p className="text-3xl font-bold text-[#10B981] font-mono">${summary.total_earned.toFixed(2)}</p>
              <div className="mt-3">
                <SparkLine data={earnings_history} color="#10B981" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                <p className="text-[10px] text-[hsl(215,16%,45%)]">Volume Bots</p>
                <p className="text-sm font-bold text-[#00CED1]">{summary.active_volume_bots} active</p>
              </div>
              <div className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                <p className="text-[10px] text-[hsl(215,16%,45%)]">Lure Bots</p>
                <p className="text-sm font-bold text-[#E8520F]">{summary.active_lure_bots} active</p>
              </div>
            </div>
            {/* Sub-User Bots tab REMOVED — for sub-users, the volume/lure counts above ARE their bots */}
          </CardContent>
        </Card>
      </div>

      {/* Bottom row — Exchange Breakdown + Connected Wallets + Recent Bots */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Exchange Breakdown */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none" data-testid="exchange-breakdown-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#6366F1]" /> By Exchange
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(exchangeManagePath)} className="text-xs text-[#E8520F] h-7 px-2" data-testid="go-exchange-connect">
                Manage <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {exchange_performance.length > 0 ? (
              <div className="space-y-3">
                {exchange_performance.map(ex => {
                  const color = EXCHANGE_COLORS[ex.name] || '#E8520F';
                  return (
                    <div key={ex.name} className="p-3 rounded-lg border border-[hsl(220,16%,18%)] hover:border-[#CBD5E1] transition-colors"
                      data-testid={`exchange-perf-${ex.name.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                            {ex.name.charAt(0)}
                          </div>
                          <p className="text-sm font-semibold text-white">{ex.name}</p>
                        </div>
                        <span className="text-[10px] text-[hsl(215,16%,45%)]">{ex.running}/{ex.bots} bots</span>
                      </div>
                      <MiniBar value={ex.total_volume} max={maxExVol} color={color} />
                      <div className="flex items-center justify-between mt-1.5 text-xs">
                        <span className="text-[hsl(215,16%,56%)]">Vol: ${ex.total_volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span className={ex.total_pnl >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                          PnL: {ex.total_pnl >= 0 ? '+' : ''}${ex.total_pnl.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(215,16%,45%)]">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No exchange activity yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connected Wallets */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none" data-testid="wallets-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#F59E0B]" /> Wallets & Connections
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {exchange_connections.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[10px] text-[hsl(215,16%,45%)] uppercase tracking-wider">Exchange Sessions</p>
                {exchange_connections.map((ec, i) => {
                  const color = EXCHANGE_COLORS[ec.exchange_name] || '#E8520F';
                  return (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                      <div className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: color }}>
                        {(ec.exchange_name || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white">{ec.exchange_name}</p>
                        <p className="text-[10px] text-[hsl(215,16%,45%)] font-mono truncate">{ec.wallet_address}</p>
                      </div>
                      <Circle className="w-2 h-2 text-[#10B981] fill-[#10B981]" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400">No exchanges connected yet</p>
                <Button variant="link" size="sm" onClick={() => navigate(exchangeManagePath)}
                  className="text-xs text-[#E8520F] p-0 h-auto mt-1" data-testid="connect-exchange-link">
                  <Link2 className="w-3 h-3 mr-1" /> Connect Exchange
                </Button>
              </div>
            )}

            {wallets.length > 0 && (
              <div className="space-y-2 mt-3">
                <p className="text-[10px] text-[hsl(215,16%,45%)] uppercase tracking-wider">Wallet Addresses</p>
                {wallets.map((w, i) => (
                  <div key={i} className="p-2 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        w.chain === 'solana' ? 'bg-[#9945FF]/10 text-[#9945FF]' : 'bg-[#627EEA]/10 text-[#627EEA]'
                      }`}>{w.chain.toUpperCase()}</span>
                      <p className="text-xs font-mono text-white truncate">{w.address}</p>
                    </div>
                    {Object.keys(w.balances).length > 0 && (
                      <div className="flex gap-3 mt-1">
                        {Object.entries(w.balances).map(([token, amount]) => (
                          <span key={token} className="text-[10px] text-[hsl(215,16%,56%)]">{token}: {typeof amount === 'number' ? amount.toFixed(4) : amount}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {wallets.length === 0 && exchange_connections.length === 0 && (
              <div className="text-center py-6 text-[hsl(215,16%,45%)]">
                <Wallet className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No wallets connected</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Bots */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none" data-testid="recent-bots-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Bot className="w-5 h-5 text-[#00CED1]" /> Recent Bots
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_bots.length > 0 ? (
              <div className="space-y-2">
                {recent_bots.map((bot, i) => {
                  const isRunning = bot.status === 'running' || bot.status === 'RUNNING';
                  return (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-[hsl(220,16%,18%)] hover:bg-[hsl(220,16%,14%)] transition-colors"
                      data-testid={`recent-bot-${bot.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        bot.type === 'VOLUME' ? 'bg-[#00CED1]/10' : 'bg-[#E8520F]/10'
                      }`}>
                        {bot.type === 'VOLUME' ? <BarChart3 className="w-4 h-4 text-[#00CED1]" /> : <Target className="w-4 h-4 text-[#E8520F]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold text-white">{bot.type}</p>
                          {bot.token !== '?' && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-[hsl(220,16%,14%)] text-[hsl(215,16%,56%)] font-medium">{bot.token}</span>
                          )}
                        </div>
                        <p className="text-[10px] text-[hsl(215,16%,45%)] truncate">{bot.exchange}</p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-[#10B981]' : 'bg-[#94A3B8]'}`} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(215,16%,45%)]">
                <Bot className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No bots launched yet</p>
                <p className="text-[10px] mt-1">Head to Volume Bot or Lure Bot to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
